/**
 * 관리자 관리 React Query hooks
 *
 * 센터의 관리자(admin) 초대, 제거, 목록 조회를 위한 hooks
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { httpsCallable } from "firebase/functions";
import { getFirebaseFunctions } from "@/config/firebase";

// === Types ===

interface InviteAdminRequest {
  email: string;
  name: string;
  initialPassword: string;
}

interface InviteAdminResponse {
  success: boolean;
  admin?: {
    uid: string;
    name: string;
  };
  error?: {
    code: string;
    message: string;
  };
}

interface RemoveAdminRequest {
  adminUid: string;
}

interface RemoveAdminResponse {
  success: boolean;
  error?: {
    code: string;
    message: string;
  };
}

interface AdminInfo {
  uid: string;
  name: string;
  email?: string;
  joinedAt: string;
}

interface ListAdminsResponse {
  success: boolean;
  admins?: AdminInfo[];
}

// === Hooks ===

/**
 * 관리자 초대 mutation
 */
export function useInviteAdmin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: InviteAdminRequest) => {
      const fn = httpsCallable<InviteAdminRequest, InviteAdminResponse>(
        getFirebaseFunctions(),
        "inviteAdmin",
      );
      const result = await fn(request);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admins"] });
    },
  });
}

/**
 * 관리자 제거 mutation
 */
export function useRemoveAdmin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: RemoveAdminRequest) => {
      const fn = httpsCallable<RemoveAdminRequest, RemoveAdminResponse>(
        getFirebaseFunctions(),
        "removeAdmin",
      );
      const result = await fn(request);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admins"] });
    },
  });
}

/**
 * 관리자 목록 조회 query
 */
export function useListAdmins(centerId: number | undefined) {
  return useQuery({
    queryKey: ["admins", centerId],
    queryFn: async () => {
      const fn = httpsCallable<void, ListAdminsResponse>(getFirebaseFunctions(), "listAdmins");
      const result = await fn();
      return result.data;
    },
    enabled: centerId !== undefined,
    staleTime: 1000 * 60 * 5,
  });
}
